import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CrmBoardDto,
  CrmBoardQuery,
  CrmComposeMessageBody,
  CrmComposeMessageDto,
  CrmCreateManualLeadBody,
  CrmDealDto,
  CrmDealStage,
  CrmPartyMergeBody,
} from '@upme/contracts';
import {
  CrmComposeMessageDto as CrmComposeMessageDtoSchema,
  CrmDealDto as CrmDealDtoSchema,
  CrmPartyMergeResultDto,
  CrmSuggestionsDto as CrmSuggestionsDtoSchema,
} from '@upme/contracts';
import { api } from '../../app/api-client';
import { crmBoardKey, crmSuggestionsKey, normalizeCrmBoardQuery } from './queries';

export async function composeCrmHintMessage(body: CrmComposeMessageBody): Promise<CrmComposeMessageDto> {
  const data = await api.post('admin/crm/suggestions/compose', { json: body }).json();
  return CrmComposeMessageDtoSchema.parse(data);
}

export function useCreateCrmManualLead(filters: CrmBoardQuery) {
  const queryClient = useQueryClient();
  const queryKey = crmBoardKey(filters);

  return useMutation({
    mutationFn: async (body: CrmCreateManualLeadBody) => {
      const data = await api.post('admin/crm/manual-leads', { json: body }).json();
      return CrmDealDtoSchema.parse(data);
    },
    onSuccess: (deal: CrmDealDto) => {
      const previous = queryClient.getQueryData<CrmBoardDto>(queryKey);
      if (previous && !previous.deals.some((d) => d.id === deal.id)) {
        queryClient.setQueryData<CrmBoardDto>(queryKey, {
          ...previous,
          deals: [deal, ...previous.deals],
          countsBySegment: {
            ...previous.countsBySegment,
            all: previous.countsBySegment.all + 1,
          },
        });
      }
      void queryClient.invalidateQueries({ queryKey: crmBoardKey(filters) });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'board'] });
    },
  });
}

export function useMoveCrmDealStage(filters: CrmBoardQuery) {
  const queryClient = useQueryClient();
  const queryKey = crmBoardKey(filters);

  return useMutation({
    mutationFn: async (input: { dealId: string; stage: CrmDealStage; sourceKey: string }) => {
      return api
        .patch(`admin/crm/deals/${input.dealId}/stage`, {
          json: { stage: input.stage, sourceKey: input.sourceKey },
        })
        .json<CrmBoardDto['deals'][number]>();
    },
    onMutate: async ({ dealId, stage }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CrmBoardDto>(queryKey);
      if (previous) {
        queryClient.setQueryData<CrmBoardDto>(queryKey, {
          ...previous,
          deals: previous.deals.map((d) => (d.id === dealId ? { ...d, stage } : d)),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: crmBoardKey(filters) });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'board'] });
    },
  });
}

export function useDismissCrmHint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hintRowId: string) => {
      await api.patch(`admin/crm/suggestions/${hintRowId}/status`, { json: { status: 'dismissed' } });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: crmSuggestionsKey() }),
  });
}

export function useRefreshCrmSuggestions(filters: CrmBoardQuery) {
  const queryClient = useQueryClient();
  const queryKey = crmSuggestionsKey();

  return useMutation({
    mutationFn: async () => {
      const data = await api
        .post('admin/crm/suggestions/refresh', {
          searchParams: boardSearchParams(normalizeCrmBoardQuery(filters)),
        })
        .json();
      return CrmSuggestionsDtoSchema.parse(data);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });
}

export function mergeCrmPartiesMutation() {
  return {
    mutationFn: async (body: CrmPartyMergeBody) => {
      const data = await api.post('admin/crm/parties/merge', { json: body }).json();
      return CrmPartyMergeResultDto.parse(data);
    },
  };
}

export function useMergeCrmParties(filters: CrmBoardQuery) {
  const queryClient = useQueryClient();
  return useMutation({
    ...mergeCrmPartiesMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: crmBoardKey(filters) });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'parties'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'crm', 'conversations'] });
    },
  });
}

function boardSearchParams(query: ReturnType<typeof normalizeCrmBoardQuery>): Record<string, string> {
  const params: Record<string, string> = { segment: query.segment };
  if (query.cityId) params.cityId = query.cityId;
  if (query.partnershipStatus) params.partnershipStatus = query.partnershipStatus;
  return params;
}
